import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/constants";
import { toast } from "sonner";

/**
 * Discovery 2.0 Sektion (Phase 3a — intern-only).
 *
 * Zeigt pro Projekt die letzten Discovery-Sessions inkl. ihrer Hypothesen-Liste,
 * mit der Option (für intern_architekt/admin), neue Hypothesen via LLM-Gateway
 * generieren zu lassen und einzelne Hypothesen zu entscheiden (bestätigt /
 * korrigiert / verworfen).
 *
 * Bewusst minimal: Sessions/Inputs werden nur referenziert (read-only Stand),
 * kein eigenes Anlage-Workflow für Sessions in dieser Iteration. Für UX-Aktion
 * "Session anlegen" gibt es einen kleinen Button, der eine intern_delivery-
 * Default-Session mit mode='generic' erzeugt — der Vollausbau (Interview-/
 * Swipe-/Confirm-Modus) folgt in Phase 3b.
 */

const STATUS_BADGE = {
  vorgeschlagen: "bg-amber-50 border-amber-200 text-amber-900",
  bestaetigt: "bg-emerald-50 border-emerald-200 text-emerald-900",
  korrigiert: "bg-blue-50 border-blue-200 text-blue-900",
  verworfen: "bg-neutral-100 border-neutral-300 text-neutral-700",
};

const SESSION_STATUS_BADGE = {
  offen: "bg-neutral-100 text-neutral-700",
  in_arbeit: "bg-amber-50 text-amber-900",
  abgeschlossen: "bg-emerald-50 text-emerald-900",
  abgebrochen: "bg-neutral-100 text-neutral-500",
};

// Problem-DNA-Dimensionen (Spec). Reihenfolge bestimmt UI-Anordnung.
// Labels werden zusätzlich vom Backend (/api/discovery/dimensions) geliefert,
// die Konstante hier ist die Single-Source für UI-Reihenfolge + Fallback.
const DIMENSION_ORDER = [
  "capture", "decision", "execution",
  "evidence", "exceptions", "reporting",
  "unklassifiziert",
];
const DIMENSION_LABELS = {
  capture: "Wie werden Daten erfasst?",
  decision: "Wer entscheidet was?",
  execution: "Wie wird gearbeitet?",
  evidence: "Was muss nachweisbar sein?",
  exceptions: "Was kann schiefgehen?",
  reporting: "Was muss sichtbar sein?",
  unklassifiziert: "Noch nicht klassifiziert",
};
const DIMENSION_TINT = {
  capture: "border-sky-200 bg-sky-50/50",
  decision: "border-violet-200 bg-violet-50/50",
  execution: "border-amber-200 bg-amber-50/50",
  evidence: "border-emerald-200 bg-emerald-50/50",
  exceptions: "border-rose-200 bg-rose-50/50",
  reporting: "border-indigo-200 bg-indigo-50/50",
  unklassifiziert: "border-neutral-200 bg-neutral-50",
};

export const Discovery2Section = ({ projectId }) => {
  const { hasRole } = useAuth();
  const canManage = hasRole("intern_architekt", "admin");

  const [sessions, setSessions] = useState([]);
  const [hypothesesBySession, setHypothesesBySession] = useState({});
  const [inputsBySession, setInputsBySession] = useState({});
  const [summariesBySession, setSummariesBySession] = useState({});
  const [llmConfigs, setLlmConfigs] = useState([]);
  const [activeTemplates, setActiveTemplates] = useState([]);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [generatingMultiFor, setGeneratingMultiFor] = useState(null);
  const [decideFor, setDecideFor] = useState(null);
  const [showNewSession, setShowNewSession] = useState(false);
  const [inputModalFor, setInputModalFor] = useState(null);
  const [draftFor, setDraftFor] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);

  const loadAll = useCallback(async () => {
    const [s, c, t] = await Promise.all([
      api.get(`/discovery/sessions?project_id=${projectId}`).catch(() => ({ data: [] })),
      api.get(`/llm-configs`).catch(() => ({ data: [] })),
      api.get(`/discovery/prompt-templates?purpose=hypothesis_generation`).catch(() => ({ data: [] })),
    ]);
    setSessions(s.data);
    setLlmConfigs(c.data);
    setActiveTemplates(t.data.filter((tpl) => tpl.status === "active"));
    // Hypothesen + Inputs + Summaries pro Session laden
    const hyp = {};
    const inp = {};
    const sums = {};
    await Promise.all(
      s.data.map(async (sess) => {
        const [hr, ir, sr] = await Promise.all([
          api.get(`/discovery/sessions/${sess.session_id}/hypotheses`).catch(() => ({ data: [] })),
          api.get(`/discovery/sessions/${sess.session_id}/inputs`).catch(() => ({ data: [] })),
          api.get(`/discovery/sessions/${sess.session_id}/summaries`).catch(() => ({ data: [] })),
        ]);
        hyp[sess.session_id] = hr.data;
        inp[sess.session_id] = ir.data;
        sums[sess.session_id] = sr.data;
      })
    );
    setHypothesesBySession(hyp);
    setInputsBySession(inp);
    setSummariesBySession(sums);
  }, [projectId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateSessionStatus = async (sessionId, newStatus) => {
    try {
      await api.patch(`/discovery/sessions/${sessionId}`, { status: newStatus });
      toast.success(`Session-Status: ${newStatus}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Statuswechsel fehlgeschlagen");
    }
  };

  const createSession = async () => {
    setShowNewSession(true);
  };

  return (
    <div className="mt-10" data-testid="dossier-discovery2">
      <div className="flex items-baseline justify-between mb-1">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Discovery 2.0</div>
          <div className="text-xs text-neutral-500 mt-0.5">
            LLM-gestützte Hypothesen aus erfassten Inputs (Phase 3a — intern). Eingefrorene Sessions akzeptieren keine Inputs/Generierungen.
          </div>
        </div>
        {canManage && (
          <button
            data-testid="d2-new-session-btn"
            onClick={createSession}
            className="text-xs font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-3 py-1.5 hover:bg-neutral-50"
          >
            + Session
          </button>
        )}
      </div>

      {sessions.length === 0 ? (
        <div className="bg-white border border-neutral-200 px-5 py-8 text-center text-sm text-neutral-500 mt-3" data-testid="d2-empty">
          Noch keine Discovery-Session angelegt.
        </div>
      ) : (
        <div className="space-y-3 mt-3">
          {sessions.map((sess) => (
            <SessionRow
              key={sess.session_id}
              session={sess}
              hypotheses={hypothesesBySession[sess.session_id] || []}
              inputs={inputsBySession[sess.session_id] || []}
              summaries={summariesBySession[sess.session_id] || []}
              canManage={canManage}
              llmConfigs={llmConfigs}
              activeTemplates={activeTemplates}
              onGenerate={() => setGeneratingFor(sess)}
              onGenerateMulti={() => setGeneratingMultiFor(sess)}
              onDecide={(hyp) => setDecideFor(hyp)}
              onAddInput={() => setInputModalFor(sess)}
              onUpdateStatus={(newStatus) => updateSessionStatus(sess.session_id, newStatus)}
              onInputsChanged={loadAll}
              onCreateDraft={() => setDraftFor(sess)}
              onTriggerReview={() => setReviewFor(sess)}
              onSummaryChanged={loadAll}
            />
          ))}
        </div>
      )}

      {showNewSession && (
        <NewSessionModal
          projectId={projectId}
          onClose={() => setShowNewSession(false)}
          onDone={() => { setShowNewSession(false); loadAll(); }}
        />
      )}

      {inputModalFor && (
        <InputModal
          session={inputModalFor}
          hypotheses={hypothesesBySession[inputModalFor.session_id] || []}
          onClose={() => setInputModalFor(null)}
          onDone={() => { setInputModalFor(null); loadAll(); }}
        />
      )}

      {generatingFor && (
        <GenerateModal
          session={generatingFor}
          llmConfigs={llmConfigs}
          activeTemplates={activeTemplates}
          onClose={() => setGeneratingFor(null)}
          onDone={() => { setGeneratingFor(null); loadAll(); }}
        />
      )}

      {generatingMultiFor && (
        <GenerateMultiModal
          session={generatingMultiFor}
          llmConfigs={llmConfigs}
          onClose={() => setGeneratingMultiFor(null)}
          onDone={() => { setGeneratingMultiFor(null); loadAll(); }}
        />
      )}

      {decideFor && (
        <DecideModal
          hypothesis={decideFor}
          onClose={() => setDecideFor(null)}
          onDone={() => { setDecideFor(null); loadAll(); }}
        />
      )}

      {draftFor && (
        <DraftSummaryModal
          session={draftFor}
          llmConfigs={llmConfigs}
          onClose={() => setDraftFor(null)}
          onDone={() => { setDraftFor(null); loadAll(); }}
        />
      )}

      {reviewFor && (
        <ReviewSummaryModal
          session={reviewFor}
          llmConfigs={llmConfigs}
          onClose={() => setReviewFor(null)}
          onDone={() => { setReviewFor(null); loadAll(); }}
        />
      )}
    </div>
  );
};

const SessionRow = ({ session, hypotheses, inputs, summaries, canManage, llmConfigs, activeTemplates, onGenerate, onGenerateMulti, onDecide, onAddInput, onUpdateStatus, onInputsChanged, onCreateDraft, onTriggerReview, onSummaryChanged }) => {
  const isFrozen = session.status === "abgeschlossen" || session.status === "abgebrochen";
  const canGenerate = canManage && !isFrozen && llmConfigs.length > 0 && activeTemplates.length > 0;
  const canAddInput = canManage && !isFrozen;
  return (
    <div className="bg-white border border-neutral-200" data-testid={`d2-session-${session.session_id}`}>
      <div className="px-5 py-3 border-b border-neutral-100 flex items-center gap-3 flex-wrap">
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${SESSION_STATUS_BADGE[session.status] || "bg-neutral-100"}`}>
          {session.status}
        </span>
        <span className="font-mono text-xs text-neutral-700">{session.mode}</span>
        <span className="text-xs text-neutral-500">{session.participant_role}</span>
        <span className="font-mono text-[10px] text-neutral-400">{fmtDate(session.created_at)}</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          {session.insight_status}
        </span>
        {canManage && !isFrozen && session.status === "offen" && (
          <button
            data-testid={`d2-session-start-${session.session_id}`}
            onClick={() => onUpdateStatus("in_arbeit")}
            className="ml-auto text-[11px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
          >
            Starten
          </button>
        )}
        {canManage && !isFrozen && session.status === "in_arbeit" && (
          <button
            data-testid={`d2-session-finish-${session.session_id}`}
            onClick={() => onUpdateStatus("abgeschlossen")}
            className="ml-auto text-[11px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
          >
            Abschließen
          </button>
        )}
      </div>

      {/* Inputs-Block — gruppiert nach Problem-DNA-Dimension */}
      <div className="px-5 py-3 border-b border-neutral-100">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
            Inputs nach Dimension ({inputs.length})
          </div>
          {canAddInput && (
            <button
              data-testid={`d2-add-input-${session.session_id}`}
              onClick={onAddInput}
              className="text-[11px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
            >
              + Input
            </button>
          )}
        </div>
        {inputs.length === 0 ? (
          <div className="text-xs text-neutral-500 italic" data-testid={`d2-inputs-empty-${session.session_id}`}>
            Noch keine Inputs erfasst.
          </div>
        ) : (
          <DimensionBuckets
            sessionId={session.session_id}
            inputs={inputs}
            canManage={canManage && !isFrozen}
            onChanged={onInputsChanged}
          />
        )}
      </div>

      <div className="px-5 py-4">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
            Hypothesen ({hypotheses.length})
          </div>
          {canGenerate && (
            <div className="flex gap-2">
              <button
                data-testid={`d2-generate-multi-btn-${session.session_id}`}
                onClick={onGenerateMulti}
                className="text-[11px] font-mono uppercase tracking-widest border border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-700 px-2 py-1"
              >
                Multi-AI generieren
              </button>
              <button
                data-testid={`d2-generate-btn-${session.session_id}`}
                onClick={onGenerate}
                className="text-[11px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
              >
                Single-AI
              </button>
            </div>
          )}
        </div>
        {hypotheses.length === 0 ? (
          <div className="text-sm text-neutral-500 italic" data-testid={`d2-hyp-empty-${session.session_id}`}>
            Noch keine Hypothesen generiert.
            {!canGenerate && llmConfigs.length === 0 && " (Keine LLM-Konfiguration vorhanden — bitte unter Settings hinterlegen.)"}
            {!canGenerate && activeTemplates.length === 0 && llmConfigs.length > 0 && " (Kein aktives Template mit purpose='hypothesis_generation' vorhanden.)"}
          </div>
        ) : (
          <ol className="space-y-2">
            {hypotheses.map((h) => (
              <HypothesisListItem
                key={h.hypothesis_id}
                h={h}
                canManage={canManage}
                onDecide={onDecide}
              />
            ))}
          </ol>
        )}
      </div>

      {/* Summary-Block (Prompt 6: Draft → 4-Rollen-Review → Approve) */}
      <SummarySection
        session={session}
        summaries={summaries}
        canManage={canManage && !isFrozen}
        canCreateDraft={canManage && !isFrozen && llmConfigs.length > 0}
        onCreateDraft={onCreateDraft}
        onTriggerReview={onTriggerReview}
        onChanged={onSummaryChanged}
      />
    </div>
  );
};

const GenerateModal = ({ session, llmConfigs, activeTemplates, onClose, onDone }) => {
  const supportedConfigs = llmConfigs.filter((c) =>
    ["openai", "anthropic", "google"].includes(c.provider)
  );
  const [configId, setConfigId] = useState(supportedConfigs[0]?.config_id || "");
  const [templateId, setTemplateId] = useState(activeTemplates[0]?.template_id || "");
  const [maxHyp, setMaxHyp] = useState(5);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!configId || !templateId) return;
    setBusy(true);
    try {
      const r = await api.post(`/discovery/sessions/${session.session_id}/hypotheses/generate`, {
        config_id: configId,
        template_id: templateId,
        max_hypotheses: Number(maxHyp),
      });
      toast.success(`${r.data.generated_count} Hypothese(n) generiert.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="d2-generate-modal">
      <div className="bg-white border border-neutral-300 max-w-lg w-full">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-widest text-neutral-700">Hypothesen generieren</div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="d2-generate-close">×</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">LLM-Konfiguration</label>
            <select
              data-testid="d2-generate-config"
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
            >
              {supportedConfigs.length === 0 && <option value="">Keine unterstützte Konfiguration</option>}
              {supportedConfigs.map((c) => (
                <option key={c.config_id} value={c.config_id}>{c.name} · {c.provider}/{c.model}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Aktives Template</label>
            <select
              data-testid="d2-generate-template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
            >
              {activeTemplates.length === 0 && <option value="">Kein aktives Hypothesen-Template</option>}
              {activeTemplates.map((t) => (
                <option key={t.template_id} value={t.template_id}>{t.name} · v{t.version}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Max. Hypothesen</label>
            <input
              data-testid="d2-generate-max"
              type="number"
              min={1}
              max={20}
              value={maxHyp}
              onChange={(e) => setMaxHyp(e.target.value)}
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-widest text-neutral-600 px-3 py-1.5 hover:text-neutral-900">
            Abbrechen
          </button>
          <button
            data-testid="d2-generate-submit"
            disabled={busy || !configId || !templateId}
            onClick={submit}
            className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-3 py-1.5"
          >
            {busy ? "Generiere …" : "Generieren"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DecideModal = ({ hypothesis, onClose, onDone }) => {
  const [decision, setDecision] = useState("bestaetigt");
  const [correction, setCorrection] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!note.trim()) {
      toast.error("change_note ist Pflicht.");
      return;
    }
    if (decision === "korrigiert" && !correction.trim()) {
      toast.error("correction_text ist bei 'korrigiert' Pflicht.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/discovery/hypotheses/${hypothesis.hypothesis_id}/decide`, {
        decision,
        correction_text: decision === "korrigiert" ? correction : null,
        change_note: note,
      });
      toast.success("Hypothese entschieden — eingefroren.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Entscheidung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="d2-decide-modal">
      <div className="bg-white border border-neutral-300 max-w-lg w-full">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-widest text-neutral-700">Hypothese entscheiden</div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="d2-decide-close">×</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="bg-neutral-50 border border-neutral-200 px-3 py-2 text-sm text-neutral-900">
            {hypothesis.hypothesis_text}
          </div>
          <div className="space-y-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block">Entscheidung</label>
            {["bestaetigt", "korrigiert", "verworfen"].map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm">
                <input
                  data-testid={`d2-decide-${opt}`}
                  type="radio"
                  name="decision"
                  value={opt}
                  checked={decision === opt}
                  onChange={() => setDecision(opt)}
                />
                {opt}
              </label>
            ))}
          </div>
          {decision === "korrigiert" && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Korrigierte Formulierung</label>
              <textarea
                data-testid="d2-decide-correction"
                value={correction}
                onChange={(e) => setCorrection(e.target.value)}
                rows={3}
                className="w-full border border-neutral-300 px-3 py-2 text-sm"
                placeholder="Korrigierte Hypothese …"
              />
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Begründung (Pflicht)</label>
            <textarea
              data-testid="d2-decide-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
              placeholder="Audit-Spur: Warum diese Entscheidung?"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-widest text-neutral-600 px-3 py-1.5 hover:text-neutral-900">
            Abbrechen
          </button>
          <button
            data-testid="d2-decide-submit"
            disabled={busy}
            onClick={submit}
            className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-3 py-1.5"
          >
            {busy ? "Speichere …" : "Entscheiden"}
          </button>
        </div>
      </div>
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────────
// NewSessionModal — Phase 3b
// ──────────────────────────────────────────────────────────────────────────
const SESSION_MODES = [
  { id: "interview", label: "Interview", hint: "Frei-Text-Inputs zu definierten Themen" },
  { id: "swipe", label: "Swipe", hint: "Aussagen mit Zustimmung/Ablehnung markieren" },
  { id: "review", label: "Review", hint: "Bestehende Inhalte durchsehen" },
  { id: "confirm", label: "Confirm", hint: "Hypothesen bestätigen / korrigieren" },
  { id: "generic", label: "Generisch", hint: "Frei strukturierter Input-Pool" },
];
const PARTICIPANT_ROLES = [
  { id: "intern_delivery", label: "intern_delivery" },
  { id: "intern_architekt", label: "intern_architekt" },
  { id: "kunde_entscheider", label: "kunde_entscheider" },
  { id: "kunde_mitwirkend", label: "kunde_mitwirkend" },
];

const NewSessionModal = ({ projectId, onClose, onDone }) => {
  const [mode, setMode] = useState("interview");
  const [role, setRole] = useState("intern_delivery");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/discovery/sessions`, {
        project_id: projectId,
        participant_role: role,
        mode,
      });
      toast.success(`Session (${mode}) angelegt.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anlage fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="d2-new-session-modal">
      <div className="bg-white border border-neutral-300 max-w-lg w-full">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-widest text-neutral-700">Neue Discovery-Session</div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="d2-new-session-close">×</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Modus</label>
            <div className="space-y-1.5">
              {SESSION_MODES.map((m) => (
                <label key={m.id} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded">
                  <input
                    data-testid={`d2-new-session-mode-${m.id}`}
                    type="radio"
                    name="mode"
                    value={m.id}
                    checked={mode === m.id}
                    onChange={() => setMode(m.id)}
                    className="mt-0.5"
                  />
                  <span className="flex-1">
                    <span className="font-medium">{m.label}</span>
                    <span className="font-mono text-[10px] text-neutral-500 ml-2">{m.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Teilnehmer-Rolle</label>
            <select
              data-testid="d2-new-session-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
            >
              {PARTICIPANT_ROLES.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-widest text-neutral-600 px-3 py-1.5 hover:text-neutral-900">
            Abbrechen
          </button>
          <button
            data-testid="d2-new-session-submit"
            disabled={busy}
            onClick={submit}
            className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-3 py-1.5"
          >
            {busy ? "Lege an …" : "Session anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// InputModal — modus-spezifisch (interview / swipe / confirm / review / generic)
// ──────────────────────────────────────────────────────────────────────────
const InputModal = ({ session, hypotheses, onClose, onDone }) => {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="d2-input-modal">
      <div className="bg-white border border-neutral-300 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-mono text-xs uppercase tracking-widest text-neutral-700">
            Input · {session.mode}-Modus
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="d2-input-close">×</button>
        </div>
        <div className="px-5 py-4">
          {session.mode === "interview" && <InterviewInput session={session} onDone={onDone} />}
          {session.mode === "swipe" && <SwipeInput session={session} onDone={onDone} />}
          {session.mode === "confirm" && <ConfirmInput session={session} hypotheses={hypotheses} onDone={onDone} />}
          {session.mode === "review" && <GenericInput session={session} onDone={onDone} placeholder="Review-Notiz, Beobachtung, Lücke …" />}
          {session.mode === "generic" && <GenericInput session={session} onDone={onDone} placeholder="Beliebiger Input zur Discovery …" />}
        </div>
      </div>
    </div>
  );
};

// Interview-Modus: Frei-Text-Block + section_hint (Themenbereich)
const InterviewInput = ({ session, onDone }) => {
  const [section, setSection] = useState("");
  const [text, setText] = useState("");
  const [dimension, setDimension] = useState("unklassifiziert");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) {
      toast.error("Inhalt ist Pflicht.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/discovery/sessions/${session.session_id}/inputs`, {
        section_hint: section.trim() || null,
        input_type: "text",
        raw_value: text.trim(),
        captured_from: "internal",
        dimension,
      });
      toast.success("Interview-Antwort erfasst.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eingabe fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Themenbereich (optional)</label>
        <input
          data-testid="d2-interview-section"
          type="text"
          value={section}
          onChange={(e) => setSection(e.target.value)}
          placeholder="z.B. ziel, scope, risiken …"
          className="w-full border border-neutral-300 px-3 py-2 text-sm"
        />
      </div>
      <DimensionSelect testid="d2-interview-dimension" value={dimension} onChange={setDimension} />
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Antwort</label>
        <textarea
          data-testid="d2-interview-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder="Frei-Text-Antwort des Teilnehmers …"
          className="w-full border border-neutral-300 px-3 py-2 text-sm"
        />
      </div>
      <div className="flex justify-end">
        <button
          data-testid="d2-interview-submit"
          disabled={busy}
          onClick={submit}
          className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-4 py-2"
        >
          {busy ? "Speichere …" : "Erfassen"}
        </button>
      </div>
    </div>
  );
};

// Swipe-Modus: Aussage + Zustimmung/Ablehnung
const SwipeInput = ({ session, onDone }) => {
  const [statement, setStatement] = useState("");
  const [dimension, setDimension] = useState("unklassifiziert");
  const [busy, setBusy] = useState(false);

  const swipe = async (decision) => {
    if (!statement.trim()) {
      toast.error("Aussage ist Pflicht.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/discovery/sessions/${session.session_id}/inputs`, {
        input_type: "swipe",
        raw_value: { statement: statement.trim(), decision },
        captured_from: "internal",
        dimension,
      });
      toast.success(`Swipe „${decision}" erfasst.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Swipe fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Aussage</label>
        <textarea
          data-testid="d2-swipe-statement"
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          rows={3}
          placeholder="z.B. Nutzer brauchen Offline-Modus."
          className="w-full border border-neutral-300 px-3 py-2 text-sm"
        />
      </div>
      <DimensionSelect testid="d2-swipe-dimension" value={dimension} onChange={setDimension} />
      <div className="text-xs text-neutral-500">
        Wischen Sie die Aussage:
      </div>
      <div className="grid grid-cols-3 gap-2">
        <button
          data-testid="d2-swipe-reject"
          disabled={busy}
          onClick={() => swipe("ablehnung")}
          className="text-xs font-mono uppercase tracking-widest border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-900 px-3 py-3 disabled:opacity-50"
        >
          ✗ Ablehnung
        </button>
        <button
          data-testid="d2-swipe-skip"
          disabled={busy}
          onClick={() => swipe("uebersprungen")}
          className="text-xs font-mono uppercase tracking-widest border border-neutral-300 bg-neutral-50 hover:bg-neutral-100 text-neutral-700 px-3 py-3 disabled:opacity-50"
        >
          → Skip
        </button>
        <button
          data-testid="d2-swipe-approve"
          disabled={busy}
          onClick={() => swipe("zustimmung")}
          className="text-xs font-mono uppercase tracking-widest border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 px-3 py-3 disabled:opacity-50"
        >
          ✓ Zustimmung
        </button>
      </div>
    </div>
  );
};

// Confirm-Modus: Hypothesen-Liste mit Bestätigen/Ablehnen/Korrigieren
const ConfirmInput = ({ session, hypotheses, onDone }) => {
  const [busy, setBusy] = useState(null);
  const proposed = hypotheses.filter((h) => h.status === "vorgeschlagen");

  const confirmHyp = async (hyp, response) => {
    setBusy(hyp.hypothesis_id);
    try {
      await api.post(`/discovery/sessions/${session.session_id}/inputs`, {
        input_type: "confirmation",
        raw_value: {
          hypothesis_id: hyp.hypothesis_id,
          hypothesis_text: hyp.hypothesis_text,
          response,
        },
        captured_from: "internal",
      });
      toast.success("Bestätigung erfasst.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bestätigung fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  if (proposed.length === 0) {
    return (
      <div className="text-sm text-neutral-500" data-testid="d2-confirm-empty">
        Keine vorgeschlagenen Hypothesen verfügbar. Bitte zuerst eine Generierung in einer vorherigen Session ausführen.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-neutral-500">
        Bestätige oder lehne die vorgeschlagenen Hypothesen ab. Die Antwort wird als Input erfasst (entscheidet aber nicht über die Hypothese selbst — das passiert separat im Decide-Modal).
      </div>
      {proposed.map((h) => (
        <div key={h.hypothesis_id} className="border border-neutral-200 px-4 py-3" data-testid={`d2-confirm-${h.hypothesis_id}`}>
          <div className="text-sm text-neutral-900 mb-2">{h.hypothesis_text}</div>
          <div className="flex gap-2">
            <button
              data-testid={`d2-confirm-yes-${h.hypothesis_id}`}
              disabled={busy === h.hypothesis_id}
              onClick={() => confirmHyp(h, "zustimmung")}
              className="text-[11px] font-mono uppercase tracking-widest border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 px-3 py-1.5 disabled:opacity-50"
            >
              ✓ Bestätigen
            </button>
            <button
              data-testid={`d2-confirm-no-${h.hypothesis_id}`}
              disabled={busy === h.hypothesis_id}
              onClick={() => confirmHyp(h, "ablehnung")}
              className="text-[11px] font-mono uppercase tracking-widest border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-900 px-3 py-1.5 disabled:opacity-50"
            >
              ✗ Ablehnen
            </button>
            <button
              data-testid={`d2-confirm-unsure-${h.hypothesis_id}`}
              disabled={busy === h.hypothesis_id}
              onClick={() => confirmHyp(h, "unsicher")}
              className="text-[11px] font-mono uppercase tracking-widest border border-neutral-300 bg-neutral-50 hover:bg-neutral-100 text-neutral-700 px-3 py-1.5 disabled:opacity-50"
            >
              ? Unsicher
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Generischer Frei-Text-Input
const GenericInput = ({ session, onDone, placeholder }) => {
  const [text, setText] = useState("");
  const [dimension, setDimension] = useState("unklassifiziert");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) {
      toast.error("Inhalt ist Pflicht.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/discovery/sessions/${session.session_id}/inputs`, {
        input_type: "text",
        raw_value: text.trim(),
        captured_from: "internal",
        dimension,
      });
      toast.success("Input erfasst.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eingabe fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <DimensionSelect testid="d2-generic-dimension" value={dimension} onChange={setDimension} />
      <textarea
        data-testid="d2-generic-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        placeholder={placeholder || "Input …"}
        className="w-full border border-neutral-300 px-3 py-2 text-sm"
      />
      <div className="flex justify-end">
        <button
          data-testid="d2-generic-submit"
          disabled={busy}
          onClick={submit}
          className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-4 py-2"
        >
          {busy ? "Speichere …" : "Erfassen"}
        </button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// DimensionBuckets — Drag-and-Drop zwischen den 7 Problem-DNA-Buckets
// ──────────────────────────────────────────────────────────────────────────
const DimensionBuckets = ({ sessionId, inputs, canManage, onChanged }) => {
  const [draggedId, setDraggedId] = useState(null);
  const [hoverDim, setHoverDim] = useState(null);

  const grouped = DIMENSION_ORDER.reduce((acc, dim) => {
    acc[dim] = [];
    return acc;
  }, {});
  for (const inp of inputs) {
    const dim = grouped[inp.dimension] ? inp.dimension : "unklassifiziert";
    grouped[dim].push(inp);
  }

  const onDrop = async (targetDim) => {
    if (!canManage || !draggedId) return;
    const dragged = inputs.find((i) => i.input_id === draggedId);
    setDraggedId(null);
    setHoverDim(null);
    if (!dragged || dragged.dimension === targetDim) return;
    try {
      await api.patch(`/discovery/inputs/${draggedId}/dimension`, { dimension: targetDim });
      toast.success(`Dimension → ${targetDim}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-Tagging fehlgeschlagen");
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={`d2-buckets-${sessionId}`}>
      {DIMENSION_ORDER.map((dim) => {
        const items = grouped[dim] || [];
        const isHover = hoverDim === dim && draggedId;
        return (
          <div
            key={dim}
            data-testid={`d2-bucket-${dim}`}
            onDragOver={(e) => {
              if (!canManage) return;
              e.preventDefault();
              if (hoverDim !== dim) setHoverDim(dim);
            }}
            onDragLeave={() => { if (hoverDim === dim) setHoverDim(null); }}
            onDrop={(e) => { e.preventDefault(); onDrop(dim); }}
            className={`border ${DIMENSION_TINT[dim]} ${isHover ? "ring-2 ring-neutral-900" : ""} px-2.5 py-2 min-h-[80px]`}
          >
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-700">{dim}</div>
              <div className="font-mono text-[10px] text-neutral-500">{items.length}</div>
            </div>
            <div className="text-[10px] text-neutral-500 mb-2 leading-tight">
              {DIMENSION_LABELS[dim]}
            </div>
            {items.length === 0 ? (
              <div className="text-[10px] text-neutral-400 italic" data-testid={`d2-bucket-empty-${dim}`}>
                {canManage ? "Hier ablegen …" : "—"}
              </div>
            ) : (
              <ul className="space-y-1">
                {items.map((inp) => (
                  <li
                    key={inp.input_id}
                    draggable={canManage}
                    onDragStart={() => setDraggedId(inp.input_id)}
                    onDragEnd={() => { setDraggedId(null); setHoverDim(null); }}
                    data-testid={`d2-input-${inp.input_id}`}
                    className={`bg-white border border-neutral-200 px-2 py-1.5 text-xs ${canManage ? "cursor-grab active:cursor-grabbing hover:border-neutral-400" : ""} ${draggedId === inp.input_id ? "opacity-40" : ""}`}
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{inp.input_type}</span>
                      {inp.section_hint && (
                        <span className="font-mono text-[9px] text-neutral-400">· {inp.section_hint}</span>
                      )}
                      {inp.captured_from === "customer" && (
                        <span className="font-mono text-[9px] uppercase text-amber-700">· customer</span>
                      )}
                    </div>
                    <div className="text-neutral-900 break-words">
                      {typeof inp.raw_value === "string"
                        ? inp.raw_value
                        : JSON.stringify(inp.raw_value)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────────
// DimensionSelect — wiederverwendet in Interview/Swipe/Generic-Input-Modals
// ──────────────────────────────────────────────────────────────────────────
const DimensionSelect = ({ testid, value, onChange }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
      Welche Dimension?
    </label>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-neutral-300 px-3 py-2 text-sm"
    >
      {DIMENSION_ORDER.map((dim) => (
        <option key={dim} value={dim}>
          {dim} — {DIMENSION_LABELS[dim]}
        </option>
      ))}
    </select>
  </div>
);


// ──────────────────────────────────────────────────────────────────────────
// HypothesisListItem — mit Konvergenz-Badge (Multi-AI) & Aktionen
// ──────────────────────────────────────────────────────────────────────────
const CONV_BADGE = {
  3: { class: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "3/3" },
  2: { class: "bg-amber-100 text-amber-800 border-amber-300", label: "2/3" },
  1: { class: "bg-rose-100 text-rose-800 border-rose-300", label: "1/3" },
};
const CATEGORY_TINT = {
  kernproblem: "bg-rose-50 text-rose-900 border-rose-200",
  scope: "bg-sky-50 text-sky-900 border-sky-200",
  risiko: "bg-amber-50 text-amber-900 border-amber-200",
  annahme: "bg-violet-50 text-violet-900 border-violet-200",
  ziel: "bg-emerald-50 text-emerald-900 border-emerald-200",
};

const HypothesisListItem = ({ h, canManage, onDecide }) => {
  const [expandDiv, setExpandDiv] = useState(false);
  const [editing, setEditing] = useState(false);
  const text = h.text || h.hypothesis_text;
  const conv = h.convergence;
  const badge = conv && CONV_BADGE[conv.count];

  return (
    <li className="border border-neutral-200 px-4 py-3" data-testid={`d2-hyp-${h.hypothesis_id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded flex-shrink-0 mt-0.5 ${STATUS_BADGE[h.status] || ""}`}>
          {h.status}
        </span>
        {h.category && (
          <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded flex-shrink-0 mt-0.5 ${CATEGORY_TINT[h.category] || "bg-neutral-100"}`}>
            {h.category}
          </span>
        )}
        {badge && (
          <span
            data-testid={`d2-hyp-badge-${h.hypothesis_id}`}
            title={`Konvergenz ${badge.label} · ${(conv.supporting_roles || []).join(", ")}`}
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded flex-shrink-0 mt-0.5 ${badge.class}`}
          >
            {badge.label}
          </span>
        )}
        {h.curated && (
          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded flex-shrink-0 mt-0.5 bg-blue-50 text-blue-900 border-blue-200">
            curated
          </span>
        )}
        {h.version != null && (
          <span className="font-mono text-[10px] text-neutral-400 mt-0.5">v{h.version}</span>
        )}

        <div className="min-w-0 w-full mt-1">
          {editing ? (
            <EditHypothesisInline
              hypothesis={h}
              onClose={() => setEditing(false)}
              onSaved={() => { setEditing(false); window.location.reload(); }}
            />
          ) : (
            <div className="text-sm text-neutral-900">{text}</div>
          )}
          {h.correction_text && (
            <div className="text-sm text-blue-900 mt-1">
              <span className="font-mono text-[10px] uppercase tracking-widest mr-1">Korrektur:</span>
              {h.correction_text}
            </div>
          )}
          {h.divergence_note && conv && conv.count < 3 && (
            <div className="mt-2">
              <button
                data-testid={`d2-hyp-divergence-toggle-${h.hypothesis_id}`}
                onClick={() => setExpandDiv((v) => !v)}
                className="text-[10px] font-mono uppercase tracking-widest text-neutral-600 hover:text-neutral-900 underline"
              >
                {expandDiv ? "Andere Stimmen verbergen" : "Andere Stimmen sagten →"}
              </button>
              {expandDiv && (
                <div
                  data-testid={`d2-hyp-divergence-${h.hypothesis_id}`}
                  className="mt-1.5 px-3 py-2 bg-neutral-50 border border-neutral-200 text-xs text-neutral-700 whitespace-pre-wrap"
                >
                  {h.divergence_note}
                </div>
              )}
            </div>
          )}
          {h.change_note && (
            <div className="font-mono text-[10px] text-neutral-500 mt-1">
              Note: {h.change_note}
              {h.decided_at && <> · {fmtDate(h.decided_at)}</>}
            </div>
          )}
        </div>

        {canManage && h.status === "vorgeschlagen" && !editing && (
          <div className="flex gap-1.5 ml-auto flex-shrink-0">
            <button
              data-testid={`d2-hyp-edit-${h.hypothesis_id}`}
              onClick={() => setEditing(true)}
              className="text-[11px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
            >
              Editieren
            </button>
            <button
              data-testid={`d2-decide-btn-${h.hypothesis_id}`}
              onClick={() => onDecide(h)}
              className="text-[11px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
            >
              Entscheiden
            </button>
          </div>
        )}
      </div>
    </li>
  );
};

const EditHypothesisInline = ({ hypothesis, onClose, onSaved }) => {
  const [text, setText] = useState(hypothesis.text || hypothesis.hypothesis_text || "");
  const [category, setCategory] = useState(hypothesis.category || "kernproblem");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) {
      toast.error("Text darf nicht leer sein.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/discovery/hypotheses/${hypothesis.hypothesis_id}/edit`, {
        text: text.trim(), category, curated: true,
      });
      toast.success("Hypothese editiert (curated).");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Editierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <textarea
        data-testid={`d2-hyp-edit-text-${hypothesis.hypothesis_id}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        className="w-full border border-neutral-300 px-3 py-2 text-sm"
      />
      <div className="flex items-center gap-2">
        <select
          data-testid={`d2-hyp-edit-category-${hypothesis.hypothesis_id}`}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="text-xs border border-neutral-300 px-2 py-1.5"
        >
          {["kernproblem", "scope", "risiko", "annahme", "ziel"].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button
          data-testid={`d2-hyp-edit-cancel-${hypothesis.hypothesis_id}`}
          onClick={onClose}
          className="text-[11px] font-mono uppercase tracking-widest text-neutral-600 px-2 py-1 hover:text-neutral-900"
        >
          Abbrechen
        </button>
        <button
          data-testid={`d2-hyp-edit-save-${hypothesis.hypothesis_id}`}
          disabled={busy}
          onClick={submit}
          className="text-[11px] font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-3 py-1"
        >
          {busy ? "Speichere …" : "Speichern"}
        </button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// GenerateMultiModal — Prompt 5
// ──────────────────────────────────────────────────────────────────────────
const GenerateMultiModal = ({ session, llmConfigs, onClose, onDone }) => {
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(["kritiker", "pragmatiker", "workflow-brille"]);
  const supported = llmConfigs.filter((c) => ["openai", "anthropic", "google"].includes(c.provider));
  const [configId, setConfigId] = useState(supported[0]?.config_id || "");
  const [equalsId, setEqualsId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/llm/review-roles").then((r) => setRoles(r.data || [])).catch(() => setRoles([]));
  }, []);

  const toggle = (slug) => {
    setSelected((prev) => {
      if (prev.includes(slug)) {
        return prev.filter((s) => s !== slug);
      }
      if (prev.length >= 5) {
        toast.error("Max. 5 Rollen.");
        return prev;
      }
      return [...prev, slug];
    });
  };

  const submit = async () => {
    if (selected.length < 2) {
      toast.error("Mindestens 2 Rollen wählen.");
      return;
    }
    if (!configId) {
      toast.error("LLM-Konfiguration wählen.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/discovery/sessions/${session.session_id}/hypotheses/generate-multi`,
        { roles: selected, model_config_id: configId, equals_config_id: equalsId || null },
      );
      toast.success(`v${r.data.version}: ${r.data.generated_count} Hypothesen-Cluster erzeugt.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Multi-Generierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="d2-generate-multi-modal">
      <div className="bg-white border border-neutral-300 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-mono text-xs uppercase tracking-widest text-neutral-700">
            Multi-AI Hypothesen-Generierung
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="d2-generate-multi-close">×</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">
              Rollen ({selected.length} ausgewählt, 2-5)
            </label>
            <div className="space-y-1.5">
              {roles.map((r) => {
                const sel = selected.includes(r.name);
                return (
                  <label key={r.role_id} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded">
                    <input
                      data-testid={`d2-multi-role-${r.name}`}
                      type="checkbox"
                      checked={sel}
                      onChange={() => toggle(r.name)}
                      className="mt-0.5"
                    />
                    <span className="flex-1">
                      <span className="font-medium">{r.name}</span>
                      {r.description && (
                        <span className="font-mono text-[10px] text-neutral-500 ml-2">{r.description}</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">LLM-Konfiguration (Hauptmodell)</label>
            <select
              data-testid="d2-multi-config"
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
            >
              {supported.map((c) => (
                <option key={c.config_id} value={c.config_id}>{c.name} · {c.provider}/{c.model}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Tiebreaker-LLM (optional)</label>
            <select
              data-testid="d2-multi-equals"
              value={equalsId}
              onChange={(e) => setEqualsId(e.target.value)}
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
            >
              <option value="">— ohne Tiebreaker (nur Token-Jaccard) —</option>
              {supported.map((c) => (
                <option key={c.config_id} value={c.config_id}>{c.name}</option>
              ))}
            </select>
            <div className="font-mono text-[10px] text-neutral-500 mt-1">
              Bei Wahl wird ein einzelner LLM-Call pro „semantisch gleiche?"-Entscheidung im Cluster-Algorithmus genutzt.
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2 bg-neutral-50">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-widest text-neutral-600 px-3 py-1.5 hover:text-neutral-900">
            Abbrechen
          </button>
          <button
            data-testid="d2-multi-submit"
            disabled={busy || selected.length < 2 || !configId}
            onClick={submit}
            className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-4 py-2"
          >
            {busy ? "Verdichtet … (max. 60s)" : "Multi-AI generieren"}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Summary-Workflow (Prompt 6): Draft → 4-Rollen-Review → Approve
// ─────────────────────────────────────────────────────────────────────────────
const SUMMARY_STATUS_BADGE = {
  draft: "bg-neutral-100 text-neutral-700",
  in_review: "bg-amber-50 text-amber-800 border border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border border-emerald-200",
};
const SEVERITY_BADGE = {
  info: "bg-neutral-100 text-neutral-700 border border-neutral-200",
  warn: "bg-amber-50 text-amber-800 border border-amber-200",
  block: "bg-rose-50 text-rose-800 border border-rose-200",
};
const FINDING_STATUS_LABEL = {
  open: "Offen",
  applied: "Eingearbeitet",
  ignored: "Ignoriert",
};
const REVIEW_ROLES = ["kritiker", "kunde-spiegel", "workflow-brille", "risiko"];
const REVIEW_ROLE_LABEL = {
  kritiker: "Kritiker",
  "kunde-spiegel": "Kunde-Spiegel",
  "workflow-brille": "Workflow-Brille",
  risiko: "Risiko",
};

const SummarySection = ({ session, summaries, canManage, canCreateDraft, onCreateDraft, onTriggerReview, onChanged }) => {
  const latest = summaries.length ? summaries[0] : null; // Liste ist nach version desc sortiert
  const sessionId = session.session_id;
  const isApproved = latest?.status === "approved";
  const blockOpenCount = (latest?.reviews || [])
    .flatMap((r) => r.findings || [])
    .filter((f) => f.severity === "block" && f.status === "open").length;

  return (
    <div className="px-5 py-4 border-t border-neutral-100" data-testid={`d2-summary-${sessionId}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
          Discovery-Summary {latest ? `· v${latest.version}` : ""}
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button
              data-testid={`d2-summary-draft-btn-${sessionId}`}
              disabled={!canCreateDraft}
              onClick={onCreateDraft}
              className="text-[11px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-2 py-1 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {latest ? "Neuer Draft" : "Draft generieren"}
            </button>
            {latest && !isApproved && (
              <button
                data-testid={`d2-summary-review-btn-${sessionId}`}
                onClick={onTriggerReview}
                className="text-[11px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
              >
                4-Rollen-Review
              </button>
            )}
          </div>
        )}
      </div>

      {!latest ? (
        <div className="text-xs text-neutral-500 italic" data-testid={`d2-summary-empty-${sessionId}`}>
          Noch keine Summary erzeugt.
          {!canCreateDraft && canManage && " (Keine LLM-Konfiguration vorhanden.)"}
        </div>
      ) : (
        <SummaryCard
          summary={latest}
          canManage={canManage}
          blockOpenCount={blockOpenCount}
          onChanged={onChanged}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ summary, canManage, blockOpenCount, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(summary.draft_text || "");
  const [busy, setBusy] = useState(false);
  const isApproved = summary.status === "approved";

  useEffect(() => { setDraftText(summary.draft_text || ""); }, [summary.summary_id, summary.draft_text]);

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.patch(`/discovery/summaries/${summary.summary_id}/draft`, { draft_text: draftText });
      toast.success("Draft gespeichert");
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const setFindingStatus = async (findingId, status) => {
    try {
      await api.patch(`/discovery/summaries/${summary.summary_id}/findings/${findingId}`, { status });
      toast.success(`Finding: ${FINDING_STATUS_LABEL[status]}`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Statuswechsel fehlgeschlagen");
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/discovery/summaries/${summary.summary_id}/approve`, {});
      toast.success("Summary freigegeben");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Freigabe fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-neutral-50 border border-neutral-200" data-testid={`d2-summary-card-${summary.summary_id}`}>
      <div className="px-4 py-2 border-b border-neutral-200 flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${SUMMARY_STATUS_BADGE[summary.status] || "bg-neutral-100"}`}>
          {summary.status}
        </span>
        <span className="font-mono text-[10px] text-neutral-500">v{summary.version}</span>
        <span className="font-mono text-[10px] text-neutral-400">{fmtDate(summary.created_at)}</span>
        {summary.frozen_version && (
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">
            eingefroren
          </span>
        )}
        {blockOpenCount > 0 && !isApproved && (
          <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-rose-700" data-testid={`d2-summary-block-count-${summary.summary_id}`}>
            {blockOpenCount} offene Block-Befunde
          </span>
        )}
      </div>

      {/* Draft-Text */}
      <div className="px-4 py-3 border-b border-neutral-200 bg-white">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
            {isApproved ? "Final-Text" : "Draft"}
          </div>
          {canManage && !isApproved && !editing && (
            <button
              data-testid={`d2-summary-edit-btn-${summary.summary_id}`}
              onClick={() => setEditing(true)}
              className="text-[10px] font-mono uppercase tracking-widest text-neutral-600 hover:text-neutral-900"
            >
              Editieren
            </button>
          )}
        </div>
        {editing ? (
          <>
            <textarea
              data-testid={`d2-summary-textarea-${summary.summary_id}`}
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              rows={10}
              className="w-full text-sm border border-neutral-300 px-2 py-1 font-mono"
            />
            <div className="flex gap-2 mt-2">
              <button
                data-testid={`d2-summary-save-btn-${summary.summary_id}`}
                disabled={busy || !draftText.trim()}
                onClick={saveDraft}
                className="text-[11px] font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-3 py-1"
              >
                Speichern
              </button>
              <button
                onClick={() => { setEditing(false); setDraftText(summary.draft_text || ""); }}
                className="text-[11px] font-mono uppercase tracking-widest text-neutral-600 hover:text-neutral-900 px-3 py-1"
              >
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <pre className="text-sm text-neutral-800 whitespace-pre-wrap font-sans" data-testid={`d2-summary-text-${summary.summary_id}`}>
            {isApproved ? (summary.final_text || summary.draft_text) : summary.draft_text}
          </pre>
        )}
      </div>

      {/* 4-Rollen-Review-Kacheln */}
      {summary.reviews && summary.reviews.length > 0 && (
        <div className="px-4 py-3 border-b border-neutral-200">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">
            4-Rollen-Review
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {REVIEW_ROLES.map((role) => {
              const review = summary.reviews.find((r) => r.role === role);
              return (
                <ReviewTile
                  key={role}
                  role={role}
                  review={review}
                  canManage={canManage && !isApproved}
                  onSetStatus={setFindingStatus}
                  summaryId={summary.summary_id}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Approve-Button */}
      {canManage && !isApproved && summary.reviews && summary.reviews.length > 0 && (
        <div className="px-4 py-3 bg-white flex items-center justify-end gap-2">
          {blockOpenCount > 0 && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-rose-700">
              Erst alle Block-Befunde behandeln
            </span>
          )}
          <button
            data-testid={`d2-summary-approve-btn-${summary.summary_id}`}
            disabled={busy || blockOpenCount > 0}
            onClick={approve}
            className="text-[11px] font-mono uppercase tracking-widest text-white bg-emerald-700 hover:bg-emerald-800 disabled:bg-neutral-400 disabled:cursor-not-allowed px-4 py-1.5"
          >
            {busy ? "…" : "Freigeben"}
          </button>
        </div>
      )}
      {isApproved && (
        <div className="px-4 py-3 bg-white text-[10px] font-mono uppercase tracking-widest text-emerald-800">
          Freigegeben am {fmtDate(summary.approved_at)}
        </div>
      )}
    </div>
  );
};

const ReviewTile = ({ role, review, canManage, onSetStatus, summaryId }) => {
  return (
    <div className="bg-white border border-neutral-200 p-2" data-testid={`d2-review-tile-${summaryId}-${role}`}>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-700">
          {REVIEW_ROLE_LABEL[role] || role}
        </div>
        <div className="font-mono text-[10px] text-neutral-400">
          {review ? `${(review.findings || []).length} Befunde` : "kein Review"}
        </div>
      </div>
      {!review || (review.findings || []).length === 0 ? (
        <div className="text-xs text-neutral-500 italic">Keine Befunde.</div>
      ) : (
        <ul className="space-y-1">
          {review.findings.map((f) => (
            <li key={f.id} className="text-xs flex flex-wrap items-start gap-1.5" data-testid={`d2-finding-${f.id}`}>
              <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${SEVERITY_BADGE[f.severity]}`}>
                {f.severity}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                {FINDING_STATUS_LABEL[f.status]}
              </span>
              <span className="flex-1 text-neutral-800">{f.text}</span>
              {canManage && f.status === "open" && (
                <span className="flex gap-1 ml-auto">
                  <button
                    data-testid={`d2-finding-apply-${f.id}`}
                    onClick={() => onSetStatus(f.id, "applied")}
                    className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 hover:text-emerald-900 border border-emerald-200 bg-emerald-50 px-1.5 py-0.5"
                  >
                    Einarbeiten
                  </button>
                  <button
                    data-testid={`d2-finding-ignore-${f.id}`}
                    onClick={() => onSetStatus(f.id, "ignored")}
                    className="text-[10px] font-mono uppercase tracking-widest text-neutral-700 hover:text-neutral-900 border border-neutral-300 bg-white px-1.5 py-0.5"
                  >
                    Ignorieren
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DraftSummaryModal = ({ session, llmConfigs, onClose, onDone }) => {
  const supported = llmConfigs.filter((c) => ["openai", "anthropic", "google"].includes(c.provider));
  const [configId, setConfigId] = useState(supported[0]?.config_id || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!configId) { toast.error("LLM-Konfiguration wählen"); return; }
    setBusy(true);
    try {
      await api.post(`/discovery/sessions/${session.session_id}/summary/draft`, {
        model_config_id: configId,
      });
      toast.success("Draft erzeugt");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Draft fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="d2-draft-summary-modal">
      <div className="bg-white border border-neutral-300 max-w-md w-full">
        <div className="px-5 py-3 border-b border-neutral-200">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Discovery-Summary</div>
          <div className="text-base font-medium text-neutral-900">Draft generieren</div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">LLM-Konfiguration</div>
            <select
              data-testid="d2-draft-config"
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              className="w-full text-sm border border-neutral-300 px-2 py-1"
            >
              <option value="">— wählen —</option>
              {supported.map((c) => (
                <option key={c.config_id} value={c.config_id}>{c.name} · {c.provider}/{c.model}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-neutral-500">
            Erzeugt eine kundentaugliche Zusammenfassung (max. 250 Wörter, 4-teiliger Aufbau) aus den
            erfassten Inputs und entschiedenen Hypothesen dieser Session.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2 bg-neutral-50">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-widest text-neutral-600 px-3 py-1.5 hover:text-neutral-900">
            Abbrechen
          </button>
          <button
            data-testid="d2-draft-submit"
            disabled={busy || !configId}
            onClick={submit}
            className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-4 py-2"
          >
            {busy ? "Generiert …" : "Draft generieren"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviewSummaryModal = ({ session, llmConfigs, onClose, onDone }) => {
  const supported = llmConfigs.filter((c) => ["openai", "anthropic", "google"].includes(c.provider));
  const [configId, setConfigId] = useState(supported[0]?.config_id || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!configId) { toast.error("LLM-Konfiguration wählen"); return; }
    setBusy(true);
    try {
      await api.post(`/discovery/sessions/${session.session_id}/summary/review`, {
        model_config_id: configId,
      });
      toast.success("4-Rollen-Review abgeschlossen");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="d2-review-summary-modal">
      <div className="bg-white border border-neutral-300 max-w-md w-full">
        <div className="px-5 py-3 border-b border-neutral-200">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Discovery-Summary</div>
          <div className="text-base font-medium text-neutral-900">4-Rollen-Review starten</div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">LLM-Konfiguration</div>
            <select
              data-testid="d2-review-config"
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              className="w-full text-sm border border-neutral-300 px-2 py-1"
            >
              <option value="">— wählen —</option>
              {supported.map((c) => (
                <option key={c.config_id} value={c.config_id}>{c.name} · {c.provider}/{c.model}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-neutral-500">
            Ruft <strong>kritiker</strong>, <strong>kunde-spiegel</strong>, <strong>workflow-brille</strong> und <strong>risiko</strong>
            {" "}parallel auf und sammelt Befunde (info / warn / block).
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2 bg-neutral-50">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-widest text-neutral-600 px-3 py-1.5 hover:text-neutral-900">
            Abbrechen
          </button>
          <button
            data-testid="d2-review-submit"
            disabled={busy || !configId}
            onClick={submit}
            className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-4 py-2"
          >
            {busy ? "Reviewt … (max. 60s)" : "Review starten"}
          </button>
        </div>
      </div>
    </div>
  );
};
